import React, { useCallback } from "react";
import { connect } from "react-redux";

import { faBars } from "@fortawesome/pro-solid-svg-icons/faBars";
import { faCalendarAlt } from "@fortawesome/pro-solid-svg-icons/faCalendarAlt";
import { faClock } from "@fortawesome/pro-solid-svg-icons/faClock";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Radio from "antd/es/radio";
import { FormattedMessage, useIntl } from "react-intl";
import { useHistory } from "react-router-dom";

import RadioGroup from "@app/component/input/RadioGroup";
import Path from "@app/enum/Path";
import { isCurrentUserManager, isCurrentUserOwner } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";
import useAnalytics from "@app/util/contexts/useAnalytics";

import ViewMode from "../enum/ViewMode";

import styles from "./Subheader.module.scss";

interface Props {
  isSchedulerAvailable: boolean;
  totalFilteredRecords: number;
  totalRecords: number;
  viewMode: ViewMode;
}

const Subheader: React.FC<Props> = ({
  isSchedulerAvailable,
  totalFilteredRecords,
  totalRecords,
  viewMode,
}) => {
  const analyticIssuer = useAnalytics();
  const intl = useIntl();
  const history = useHistory();

  const changeViewMode = useCallback(
    (newViewMode: ViewMode) => {
      history.push(`${Path.ACTIVITY}/${newViewMode}`);
      if (newViewMode === ViewMode.LIST) {
        analyticIssuer?.clicked(["Activities List View"]);
      } else if (newViewMode === ViewMode.SCHEDULER) {
        analyticIssuer?.clicked(["Activities Scheduler View"]);
      } else {
        analyticIssuer?.clicked(["Activities Planner View"]);
      }
    },
    [analyticIssuer, history]
  );

  return (
    <section className={styles.container}>
      <div className={styles.headingSection}>
        <div className={styles.heading}>
          <h3 className={styles.title} data-element-id="page-title">
            <FormattedMessage
              id="scene.activity.tableTitle"
              defaultMessage="Activities"
              description="A title for Activities table on Activities page"
            />
          </h3>
          <span className={styles.count}>
            <FormattedMessage
              id="scene.activity.activitiesCount"
              defaultMessage="{totalFilteredRecords} / {totalRecords} shown"
              description="Filtered count vs total count message"
              values={{
                totalFilteredRecords: intl.formatNumber(totalFilteredRecords),
                totalRecords: intl.formatNumber(totalRecords),
              }}
            />
          </span>
        </div>
        <RadioGroup onChange={changeViewMode} value={viewMode}>
          <Radio.Button key={ViewMode.CALENDAR} value={ViewMode.CALENDAR}>
            <FontAwesomeIcon className={styles.switchIcon} icon={faCalendarAlt} size="sm" />
            <FormattedMessage
              id="activities.header.viewSwitch.calendar"
              defaultMessage="Planner"
              description="Calendar view button title in view Switch on Activities scenes"
            />
          </Radio.Button>
          <Radio.Button key={ViewMode.LIST} value={ViewMode.LIST}>
            <FontAwesomeIcon className={styles.switchIcon} icon={faBars} size="sm" />
            <FormattedMessage
              id="activities.header.viewSwitch.list"
              defaultMessage="List"
              description="List view button title in view Switch on Activities scenes"
            />
          </Radio.Button>
          {isSchedulerAvailable && (
            <Radio.Button key={ViewMode.SCHEDULER} value={ViewMode.SCHEDULER}>
              <FontAwesomeIcon className={styles.switchIcon} icon={faClock} size="sm" />
              <FormattedMessage
                id="activities.header.viewSwitch.scheduler"
                defaultMessage="Scheduler"
                description="Scheduler view button title in view Switch on Activities scenes"
              />
            </Radio.Button>
          )}
        </RadioGroup>
      </div>
    </section>
  );
};

const mapStateToProps = (state: RootState) => ({
  isSchedulerAvailable: isCurrentUserManager(state) || isCurrentUserOwner(state),
});

export default connect(mapStateToProps)(Subheader);
